// src/utils/analytics.ts
import ReactGA from 'react-ga4';

// GA4測定ID
// 旧ID G-ZJMM8DWR2J はGoogle側でタグ未配信(gtag/js が404)のまま復旧しないため、
// 2026-09-04 に新ストリーム「Chusho-App v2」(G-6GRX2T3Q16) へ切替。
const GA_MEASUREMENT_ID = 'G-6GRX2T3Q16';

// 本番環境かどうかを判定（ホスト名ベース）
const isProduction = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'chusho-app.web.app' || hostname === 'chusho-app.firebaseapp.com';
};

// Analytics初期化
export const initGA = (): void => {
  if (isProduction()) {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      gaOptions: {
        siteSpeedSampleRate: 100,
      },
      gtagOptions: {
        send_page_view: false, // 手動でページビューを送信
      },
    });
  }
};

// ページビュートラッキング
export const trackPageView = (path: string, title?: string): void => {
  if (isProduction()) {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
    });
  }
};

// カスタムイベントトラッキング
interface EventParams {
  category: string;
  action: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

export const trackEvent = ({ category, action, label, value, ...rest }: EventParams): void => {
  if (isProduction()) {
    ReactGA.event({
      category,
      action,
      label,
      value,
      ...rest,
    });
  }
};

// ユーザープロパティ設定
export const setUserProperties = (properties: Record<string, any>): void => {
  if (isProduction()) {
    ReactGA.set(properties);
  }
};

// ユーザーID設定
export const setUserId = (userId: string | null): void => {
  if (!isProduction()) return;
  // GA4 の User-ID は snake_case の `user_id`。以前は camelCase の `userId` を送っており
  // GA4 側で User-ID が一切設定されていなかった(= セッションを跨いだ個人の動きが追えなかった)。
  if (userId) {
    ReactGA.set({ user_id: userId });
    ReactGA.gtag('set', 'user_properties', { signed_in: 'true' });
  } else {
    ReactGA.set({ user_id: null });
  }
};

// ====================
// 専用トラッキング関数
// ====================

// 問題関連イベント
export const trackQuestionEvent = {
  view: (questionId: string, subject: string, year?: string) => {
    trackEvent({
      category: 'Question',
      action: 'view',
      label: `${subject}_${year}_${questionId}`,
      question_id: questionId,
      subject,
      year,
    });
  },
  answer: (questionId: string, isCorrect: boolean, timeSpent: number) => {
    trackEvent({
      category: 'Question',
      action: 'answer',
      label: isCorrect ? 'correct' : 'incorrect',
      value: timeSpent,
      question_id: questionId,
      is_correct: isCorrect,
      time_spent: timeSpent,
    });
  },
  bookmark: (questionId: string, action: 'add' | 'remove') => {
    trackEvent({
      category: 'Question',
      action: `bookmark_${action}`,
      label: questionId,
      question_id: questionId,
    });
  },
  viewExplanation: (questionId: string) => {
    trackEvent({
      category: 'Question',
      action: 'view_explanation',
      label: questionId,
      question_id: questionId,
    });
  },
};

// 学習進捗イベント
export const trackLearningEvent = {
  subjectComplete: (subject: string, totalQuestions: number, correctRate: number) => {
    trackEvent({
      category: 'Learning',
      action: 'subject_complete',
      label: subject,
      value: Math.round(correctRate * 100),
      subject,
      total_questions: totalQuestions,
      correct_rate: correctRate,
    });
  },
  dailyGoalAchieved: (questionsCompleted: number) => {
    trackEvent({
      category: 'Learning',
      action: 'daily_goal_achieved',
      value: questionsCompleted,
      questions_completed: questionsCompleted,
    });
  },
  studySessionStart: (subject?: string) => {
    trackEvent({
      category: 'Learning',
      action: 'session_start',
      label: subject,
      subject,
    });
  },
  studySessionEnd: (duration: number, questionsAnswered: number) => {
    trackEvent({
      category: 'Learning',
      action: 'session_end',
      value: duration,
      session_duration: duration,
      questions_answered: questionsAnswered,
    });
  },
};

// サブスクリプション関連イベント
export const trackSubscriptionEvent = {
  viewPlans: () => {
    trackEvent({
      category: 'Subscription',
      action: 'view_plans',
    });
  },
  startCheckout: (planType: string, price: number) => {
    trackEvent({
      category: 'Subscription',
      action: 'begin_checkout',
      label: planType,
      value: price,
      plan_type: planType,
      price,
    });
  },
  purchase: (planType: string, price: number, transactionId: string) => {
    trackEvent({
      category: 'Subscription',
      action: 'purchase',
      label: planType,
      value: price,
      plan_type: planType,
      price,
      transaction_id: transactionId,
    });
    // eコマーストラッキング
    ReactGA.event('purchase', {
      transaction_id: transactionId,
      value: price,
      currency: 'JPY',
      items: [
        {
          item_id: planType,
          item_name: `Premium Plan - ${planType}`,
          price,
          quantity: 1,
        },
      ],
    });
  },
  cancel: (planType: string, reason?: string) => {
    trackEvent({
      category: 'Subscription',
      action: 'cancel',
      label: reason || 'not_specified',
      plan_type: planType,
      cancel_reason: reason,
    });
  },
};

// ユーザーエンゲージメントイベント
export const trackEngagementEvent = {
  forumPost: (postType: 'question' | 'reply') => {
    trackEvent({
      category: 'Engagement',
      action: 'forum_post',
      label: postType,
      post_type: postType,
    });
  },
  shareContent: (contentType: string, method: string) => {
    trackEvent({
      category: 'Engagement',
      action: 'share',
      label: `${contentType}_${method}`,
      content_type: contentType,
      share_method: method,
    });
  },
  featureUsed: (featureName: string) => {
    trackEvent({
      category: 'Engagement',
      action: 'feature_used',
      label: featureName,
      feature_name: featureName,
    });
  },
};

// ナビゲーションイベント
export const trackNavigationEvent = {
  bottomNavClick: (destination: string) => {
    trackEvent({
      category: 'Navigation',
      action: 'bottom_nav_click',
      label: destination,
      destination,
    });
  },
  subjectSelect: (subject: string, year?: string) => {
    trackEvent({
      category: 'Navigation',
      action: 'subject_select',
      label: `${subject}_${year}`,
      subject,
      year,
    });
  },
  externalLink: (url: string, linkText: string) => {
    trackEvent({
      category: 'Navigation',
      action: 'external_link_click',
      label: linkText,
      url,
    });
  },
};

// エラートラッキング
export const trackError = (error: Error, errorInfo?: any, isFatal: boolean = false): void => {
  if (isProduction()) {
    trackEvent({
      category: 'Error',
      action: isFatal ? 'fatal_error' : 'non_fatal_error',
      label: error.message,
      error_message: error.message,
      error_stack: error.stack?.substring(0, 500), // 文字数制限
      component_stack: errorInfo?.componentStack?.substring(0, 500),
      is_fatal: isFatal,
    });
  }
};

// スクロールトラッキング
export const trackScroll = (depth: number, page: string): void => {
  if (isProduction()) {
    trackEvent({
      category: 'Engagement',
      action: 'scroll',
      label: `${page}_${depth}%`,
      value: depth,
      page,
      scroll_depth: depth,
    });
  }
};

// タイミングトラッキング
export const trackTiming = (
  category: string,
  variable: string,
  value: number,
  label?: string
): void => {
  if (isProduction()) {
    ReactGA.event('timing_complete', {
      name: variable,
      value: Math.round(value),
      event_category: category,
      event_label: label,
    });
  }
};

// セッション開始
export const trackSessionStart = (): void => {
  if (isProduction()) {
    trackEvent({
      category: 'Session',
      action: 'session_start',
    });
  }
};

// 離脱意図検出
export const trackExitIntent = (page: string): void => {
  if (isProduction()) {
    trackEvent({
      category: 'Engagement',
      action: 'exit_intent',
      label: page,
      page,
    });
  }
};

// GTM用のdataLayer設定
declare global {
  interface Window {
    dataLayer: any[];
  }
}

export const pushToDataLayer = (data: Record<string, any>): void => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

// コンバージョンイベント（GTM経由）
export const trackConversion = (conversionType: string, value?: number): void => {
  pushToDataLayer({
    event: 'conversion',
    conversion_type: conversionType,
    conversion_value: value,
  });
};

// GA4ネイティブ形式のイベント送信（イベント名+パラメータ）。
// 課金ファネル計測用: paywall_view / checkout_click など。
export const trackGA4 = (name: string, params?: Record<string, any>): void => {
  if (isProduction()) {
    ReactGA.event(name, params);
  }
};
